import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, fmtINR } from "../lib/api";
import { toast } from "sonner";
import {
  Users, Package, ShoppingBag, IndianRupee, ShieldCheck, Plus, Trash2, MapPin,
  Settings as SettingsIcon, FileWarning, UserCog, ScrollText, Tag, Image as ImageIcon,
  Wallet, BarChart3,
} from "lucide-react";
import { INDIAN_STATES } from "../lib/india";
import { CouponsTab, BannersTab, CustomersTab, FinanceTab, ReportsTab } from "./AdminPhase3";
import { useSEO } from "../lib/useSEO";

const ALL_TABS = [
  { key: "overview", label: "Overview", icon: ShoppingBag },
  { key: "sellers", label: "Sellers", icon: ShieldCheck },
  { key: "customers", label: "Customers", icon: Users },
  { key: "categories", label: "Categories", icon: Package },
  { key: "orders", label: "Orders", icon: ShoppingBag },
  { key: "coupons", label: "Coupons", icon: Tag },
  { key: "banners", label: "Banners", icon: ImageIcon },
  { key: "finance", label: "Finance", icon: Wallet },
  { key: "reports", label: "Reports", icon: BarChart3 },
  { key: "pincodes", label: "Pincodes", icon: MapPin },
  { key: "settings", label: "Settings", icon: SettingsIcon },
  { key: "dpdp", label: "DPDP", icon: FileWarning },
  { key: "admins", label: "Admins", icon: UserCog },
  { key: "audit", label: "Audit", icon: ScrollText },
];

export default function AdminDashboard() {
  useSEO({ title: "Admin Dashboard | Joy Pearl", description: "Joy Pearl admin console — manage sellers, products, orders, finance, GSTR-1 and DPDP requests.", noIndex: true });
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");

  if (loading) return <div className="jp-container py-20 text-center text-brand-muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return (
    <div className="jp-container py-20 text-center"><h1 className="font-serif text-3xl">Admin access only</h1><Link to="/" className="jp-btn-primary mt-4 inline-flex">Back home</Link></div>
  );

  return (
    <div className="jp-container py-8 md:py-12" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="jp-eyebrow mb-2">Admin {user.admin_role && `• ${user.admin_role.replace(/_/g, " ")}`}</p>
          <h1 className="font-serif text-3xl md:text-5xl">Joy Pearl Dashboard</h1>
        </div>
      </div>

      <div className="border-b border-brand-line mt-8 mb-6 flex gap-1 overflow-x-auto">
        {ALL_TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-4 py-3 text-sm uppercase tracking-wider whitespace-nowrap flex items-center gap-2 ${tab === t.key ? "text-brand-primary border-b-2 border-brand-primary" : "text-brand-muted"}`}
                  data-testid={`admin-tab-${t.key}`}>
            <t.icon size={14}/> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview/>}
      {tab === "sellers" && <SellersTab/>}
      {tab === "customers" && <CustomersTab/>}
      {tab === "categories" && <CategoriesTab/>}
      {tab === "orders" && <OrdersTab/>}
      {tab === "coupons" && <CouponsTab/>}
      {tab === "banners" && <BannersTab/>}
      {tab === "finance" && <FinanceTab/>}
      {tab === "reports" && <ReportsTab/>}
      {tab === "pincodes" && <PincodesTab/>}
      {tab === "settings" && <SettingsTab/>}
      {tab === "dpdp" && <DpdpTab/>}
      {tab === "admins" && <AdminsTab user={user}/>}
      {tab === "audit" && <AuditTab/>}
    </div>
  );
}

// -------- Overview --------
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const StatCard = ({ icon: Icon, label, value }) => (
    <div className="bg-white border border-brand-line p-5">
      <div className="flex items-center gap-3 text-brand-muted text-xs uppercase tracking-wider"><Icon size={16} className="text-brand-primary"/>{label}</div>
      <p className="font-serif text-3xl mt-3 text-brand-ink">{value}</p>
    </div>
  );
  if (!stats) return <div className="text-brand-muted text-sm">Loading stats…</div>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard icon={Users} label="Users" value={stats.users}/>
      <StatCard icon={ShieldCheck} label="Sellers" value={`${stats.sellers} (${stats.pending_sellers} pending)`}/>
      <StatCard icon={Package} label="Products" value={stats.products}/>
      <StatCard icon={ShoppingBag} label="Orders" value={stats.orders}/>
      <StatCard icon={FileWarning} label="DPDP open" value={stats.pending_dpdp || 0}/>
      <div className="bg-brand-primary text-white p-5 md:col-span-3">
        <div className="text-xs uppercase tracking-wider text-white/80 flex items-center gap-2"><IndianRupee size={16}/>Revenue (confirmed orders)</div>
        <p className="font-serif text-4xl mt-3" data-testid="admin-revenue">{fmtINR(stats.revenue)}</p>
      </div>
    </div>
  );
}

// -------- Sellers --------
function SellersTab() {
  const [sellers, setSellers] = useState([]);
  const reload = () => api.get("/admin/sellers").then((r) => setSellers(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  const approve = async (uid, ok) => {
    try { await api.post("/admin/sellers/approve", { user_id: uid, approve: ok }); toast.success(ok ? "Approved" : "Rejected"); reload(); }
    catch { toast.error("Forbidden or failed"); }
  };
  return (
    <div className="bg-white border border-brand-line">
      <table className="w-full text-sm">
        <thead className="bg-brand-blush/30"><tr><th className="text-left p-3 font-medium">Business</th><th className="text-left p-3 font-medium">Email</th><th className="text-left p-3 font-medium">Status</th><th className="text-right p-3 font-medium">Actions</th></tr></thead>
        <tbody>
          {sellers.map((s) => (
            <tr key={s.user_id} className="border-t border-brand-line" data-testid={`admin-seller-${s.user_id}`}>
              <td className="p-3"><p className="font-medium">{s.seller_info?.business_name || s.name}</p><p className="text-xs text-brand-muted">{s.seller_info?.gstin || "No GSTIN"}</p></td>
              <td className="p-3 text-brand-muted">{s.email}</td>
              <td className="p-3"><span className="jp-badge capitalize">{s.seller_status}</span></td>
              <td className="p-3 text-right">
                {s.seller_status === "pending" ? (
                  <div className="inline-flex gap-2">
                    <button onClick={() => approve(s.user_id, true)} className="text-brand-primary text-xs uppercase" data-testid={`approve-${s.user_id}`}>Approve</button>
                    <button onClick={() => approve(s.user_id, false)} className="text-red-700 text-xs uppercase">Reject</button>
                  </div>
                ) : s.seller_status === "approved" ? (
                  <button onClick={() => approve(s.user_id, false)} className="text-red-700 text-xs uppercase">Revoke</button>
                ) : (
                  <button onClick={() => approve(s.user_id, true)} className="text-brand-primary text-xs uppercase">Re-approve</button>
                )}
              </td>
            </tr>
          ))}
          {sellers.length === 0 && <tr><td colSpan={4} className="text-center p-12 text-brand-muted">No sellers yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// -------- Categories --------
function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState({ name: "", slug: "", image_url: "", sort_order: 0 });
  const reload = () => api.get("/categories").then((r) => setCategories(r.data));
  useEffect(() => { reload(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      const slug = newCat.slug || newCat.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      await api.post("/categories", { ...newCat, slug });
      toast.success("Category created"); setNewCat({ name: "", slug: "", image_url: "", sort_order: 0 }); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete category?")) return;
    try { await api.delete(`/categories/${id}`); toast.success("Removed"); reload(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="grid md:grid-cols-[1fr_360px] gap-6">
      <div className="bg-white border border-brand-line">
        <table className="w-full text-sm">
          <thead className="bg-brand-blush/30"><tr><th className="text-left p-3 font-medium">Name</th><th className="text-left p-3 font-medium">Slug</th><th className="text-right p-3"></th></tr></thead>
          <tbody>
            {categories.map((c) => (
              <tr key={c.id} className="border-t border-brand-line">
                <td className="p-3 flex items-center gap-3">
                  <div className="h-10 w-10 bg-brand-blush overflow-hidden">{c.image_url && <img src={c.image_url} alt="" className="h-full w-full object-cover"/>}</div>
                  <span className="font-medium">{c.name}</span>
                </td>
                <td className="p-3 text-brand-muted">{c.slug}</td>
                <td className="p-3 text-right"><button onClick={() => del(c.id)} className="text-red-700"><Trash2 size={14}/></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form onSubmit={create} className="bg-white border border-brand-line p-5 h-fit space-y-3" data-testid="new-category-form">
        <h3 className="font-serif text-lg">New Category</h3>
        <input required placeholder="Name" className="jp-input" value={newCat.name} onChange={(e) => setNewCat({...newCat, name: e.target.value})} data-testid="cat-name"/>
        <input placeholder="Slug (optional)" className="jp-input" value={newCat.slug} onChange={(e) => setNewCat({...newCat, slug: e.target.value})}/>
        <input placeholder="Image URL" className="jp-input" value={newCat.image_url} onChange={(e) => setNewCat({...newCat, image_url: e.target.value})}/>
        <input type="number" placeholder="Sort order" className="jp-input" value={newCat.sort_order} onChange={(e) => setNewCat({...newCat, sort_order: parseInt(e.target.value || 0)})}/>
        <button type="submit" className="jp-btn-primary w-full"><Plus size={14} className="mr-2"/>Create</button>
      </form>
    </div>
  );
}

// -------- Orders --------
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-white border border-brand-line overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-brand-blush/30"><tr>
          <th className="text-left p-3 font-medium">Order</th>
          <th className="text-left p-3 font-medium">Customer</th>
          <th className="text-right p-3 font-medium">Total</th>
          <th className="text-left p-3 font-medium">Status</th>
          <th className="text-left p-3 font-medium hidden md:table-cell">Date</th>
        </tr></thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-t border-brand-line">
              <td className="p-3 font-medium">{o.order_number}</td>
              <td className="p-3">{o.customer_name}<br/><span className="text-xs text-brand-muted">{o.customer_phone}</span></td>
              <td className="p-3 text-right">{fmtINR(o.total)}</td>
              <td className="p-3"><span className="jp-badge capitalize">{o.status.replace("_", " ")}</span></td>
              <td className="p-3 hidden md:table-cell text-brand-muted">{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
            </tr>
          ))}
          {orders.length === 0 && <tr><td colSpan={5} className="text-center p-12 text-brand-muted">No orders yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// -------- Pincodes --------
function PincodesTab() {
  const [pins, setPins] = useState([]);
  const [form, setForm] = useState({ pincode: "", city: "", state: "", cod_available: true });
  const reload = () => api.get("/pincodes").then((r) => setPins(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(form.pincode)) { toast.error("Pincode must be 6 digits"); return; }
    try { await api.post("/pincodes", form); toast.success("Added"); setForm({ pincode: "", city: "", state: "", cod_available: true }); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (pin) => {
    if (!window.confirm(`Deactivate ${pin}?`)) return;
    try { await api.delete(`/pincodes/${pin}`); toast.success("Deactivated"); reload(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="grid md:grid-cols-[1fr_360px] gap-6">
      <div className="bg-white border border-brand-line max-h-[600px] overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-blush/30 sticky top-0"><tr>
            <th className="text-left p-3 font-medium">Pincode</th>
            <th className="text-left p-3 font-medium">City</th>
            <th className="text-left p-3 font-medium">State</th>
            <th className="text-left p-3 font-medium">COD</th>
            <th className="text-right p-3"></th>
          </tr></thead>
          <tbody>
            {pins.map((p) => (
              <tr key={p.pincode} className="border-t border-brand-line" data-testid={`pin-row-${p.pincode}`}>
                <td className="p-3 font-medium">{p.pincode}</td>
                <td className="p-3">{p.city}</td>
                <td className="p-3 text-brand-muted">{p.state}</td>
                <td className="p-3">{p.cod_available ? "Yes" : "No"}</td>
                <td className="p-3 text-right"><button onClick={() => del(p.pincode)} className="text-red-700"><Trash2 size={14}/></button></td>
              </tr>
            ))}
            {pins.length === 0 && <tr><td colSpan={5} className="text-center p-12 text-brand-muted">No serviceable pincodes yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <form onSubmit={add} className="bg-white border border-brand-line p-5 h-fit space-y-3" data-testid="new-pincode-form">
        <h3 className="font-serif text-lg">Add Serviceable Pincode</h3>
        <input required maxLength={6} pattern="\d{6}" placeholder="Pincode" className="jp-input" value={form.pincode} onChange={(e) => setForm({...form, pincode: e.target.value.replace(/\D/g, "")})} data-testid="pin-pincode"/>
        <input required placeholder="City" className="jp-input" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}/>
        <select required className="jp-input" value={form.state} onChange={(e) => setForm({...form, state: e.target.value})}>
          <option value="">Select State / UT…</option>
          {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.cod_available} onChange={(e) => setForm({...form, cod_available: e.target.checked})}/> COD available here
        </label>
        <button type="submit" className="jp-btn-primary w-full"><Plus size={14} className="mr-2"/>Add Pincode</button>
      </form>
    </div>
  );
}

// -------- Settings --------
function SettingsTab() {
  const [settings, setSettings] = useState({});
  const [editing, setEditing] = useState({});
  useEffect(() => { api.get("/settings").then((r) => { setSettings(r.data); setEditing(r.data); }).catch(() => {}); }, []);
  const save = async (key) => {
    const value = editing[key];
    const v = typeof settings[key] === "number" ? Number(value) : value;
    try { await api.put("/settings", { key, value: v }); toast.success(`${key} saved`); setSettings((s) => ({...s, [key]: v})); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const keys = ["cod_max_amount", "cod_handling_fee", "free_shipping_over", "shipping_flat", "otp_rate_limit_minutes", "grievance_email", "grievance_officer_name"];
  return (
    <div className="bg-white border border-brand-line p-6 max-w-2xl" data-testid="settings-tab">
      <h3 className="font-serif text-xl mb-4">Store Settings</h3>
      <div className="space-y-4">
        {keys.map((k) => (
          <div key={k} className="grid grid-cols-1 sm:grid-cols-[200px_1fr_80px] gap-3 items-center">
            <label className="text-sm text-brand-muted">{k.replace(/_/g, " ")}</label>
            <input className="jp-input" value={editing[k] ?? ""} onChange={(e) => setEditing({...editing, [k]: e.target.value})} data-testid={`setting-${k}`}/>
            <button onClick={() => save(k)} className="jp-btn-outline px-2 py-1 text-xs" data-testid={`save-setting-${k}`}>Save</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------- DPDP --------
function DpdpTab() {
  const [reqs, setReqs] = useState([]);
  const [filter, setFilter] = useState("");
  const reload = () => api.get(`/dpdp/admin/requests${filter ? `?status=${filter}` : ""}`).then((r) => setReqs(r.data)).catch(() => toast.error("Forbidden or failed"));
  useEffect(() => { reload(); }, [filter]);
  const updateStatus = async (id, status) => {
    const note = window.prompt("Resolution note (optional):") || "";
    try { await api.post("/dpdp/admin/requests/status", { request_id: id, status, resolution_note: note }); toast.success("Updated"); reload(); }
    catch { toast.error("Forbidden or failed"); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-xl">DPDP Requests</h3>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="jp-input border border-brand-line px-3 py-2 text-sm">
          <option value="">All</option>
          <option value="received">Received</option>
          <option value="in_progress">In progress</option>
          <option value="resolved">Resolved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
      <div className="bg-white border border-brand-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-blush/30"><tr>
            <th className="text-left p-3 font-medium">Requester</th>
            <th className="text-left p-3 font-medium">Type</th>
            <th className="text-left p-3 font-medium">Note</th>
            <th className="text-left p-3 font-medium">Status</th>
            <th className="text-left p-3 font-medium hidden md:table-cell">Requested</th>
            <th className="text-right p-3 font-medium">Action</th>
          </tr></thead>
          <tbody>
            {reqs.map((r) => (
              <tr key={r.id} className="border-t border-brand-line" data-testid={`dpdp-row-${r.id}`}>
                <td className="p-3">{r.user_name}<br/><span className="text-xs text-brand-muted">{r.user_email}</span></td>
                <td className="p-3 capitalize">{r.request_type}</td>
                <td className="p-3 text-brand-muted max-w-[200px] truncate">{r.note || "—"}</td>
                <td className="p-3"><span className="jp-badge capitalize">{r.status.replace("_", " ")}</span></td>
                <td className="p-3 hidden md:table-cell text-brand-muted">{new Date(r.requested_at).toLocaleDateString("en-IN")}</td>
                <td className="p-3 text-right">
                  <select onChange={(e) => updateStatus(r.id, e.target.value)} defaultValue="" className="text-xs border border-brand-line p-1" data-testid={`dpdp-action-${r.id}`}>
                    <option value="" disabled>Update…</option>
                    <option value="in_progress">Mark in progress</option>
                    <option value="resolved">Resolve</option>
                    <option value="rejected">Reject</option>
                  </select>
                </td>
              </tr>
            ))}
            {reqs.length === 0 && <tr><td colSpan={6} className="text-center p-12 text-brand-muted">No requests in this filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------- Admins --------
function AdminsTab({ user }) {
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", admin_role: "order_manager" });
  const [forbidden, setForbidden] = useState(false);
  const reload = () => {
    api.get("/admin/admins").then((r) => setAdmins(r.data)).catch(() => setForbidden(true));
    api.get("/admin/admins/roles").then((r) => setRoles(r.data.roles)).catch(() => {});
  };
  useEffect(() => { reload(); }, []);
  if (forbidden) return <p className="text-brand-muted text-sm" data-testid="admins-forbidden">Only the Super Admin can manage admins.</p>;
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/admins", form); toast.success("Admin created"); setForm({ email: "", name: "", password: "", admin_role: "order_manager" }); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const changeRole = async (uid, admin_role) => {
    try { await api.put("/admin/admins/role", { user_id: uid, admin_role }); toast.success("Role updated"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (uid) => {
    if (!window.confirm("Demote this admin to customer?")) return;
    try { await api.delete(`/admin/admins/${uid}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="grid md:grid-cols-[1fr_360px] gap-6">
      <div className="bg-white border border-brand-line">
        <table className="w-full text-sm">
          <thead className="bg-brand-blush/30"><tr><th className="text-left p-3 font-medium">Name</th><th className="text-left p-3 font-medium">Email</th><th className="text-left p-3 font-medium">Sub-role</th><th className="text-right p-3"></th></tr></thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.user_id} className="border-t border-brand-line" data-testid={`admin-row-${a.user_id}`}>
                <td className="p-3 font-medium">{a.name}{a.user_id === user.user_id && <span className="jp-badge ml-2">You</span>}</td>
                <td className="p-3 text-brand-muted">{a.email}</td>
                <td className="p-3">
                  <select value={a.admin_role || "super_admin"} onChange={(e) => changeRole(a.user_id, e.target.value)}
                          disabled={a.user_id === user.user_id}
                          className="text-xs border border-brand-line p-1">
                    {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                </td>
                <td className="p-3 text-right">
                  {a.user_id !== user.user_id && <button onClick={() => remove(a.user_id)} className="text-red-700"><Trash2 size={14}/></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form onSubmit={create} className="bg-white border border-brand-line p-5 h-fit space-y-3" data-testid="new-admin-form">
        <h3 className="font-serif text-lg">Add Admin</h3>
        <input required type="email" placeholder="Email" className="jp-input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}/>
        <input required placeholder="Name" className="jp-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}/>
        <input required type="password" placeholder="Temporary password" className="jp-input" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}/>
        <select className="jp-input" value={form.admin_role} onChange={(e) => setForm({...form, admin_role: e.target.value})}>
          {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <button type="submit" className="jp-btn-primary w-full"><Plus size={14} className="mr-2"/>Create Admin</button>
        <p className="text-xs text-brand-muted">Only Super Admin can create or modify admins.</p>
      </form>
    </div>
  );
}

// -------- Audit Log --------
function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setLogs(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-white border border-brand-line overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-brand-blush/30"><tr>
          <th className="text-left p-3 font-medium">When</th>
          <th className="text-left p-3 font-medium">Actor</th>
          <th className="text-left p-3 font-medium">Action</th>
          <th className="text-left p-3 font-medium">Target</th>
        </tr></thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.log_id} className="border-t border-brand-line text-xs">
              <td className="p-3 text-brand-muted">{new Date(l.at).toLocaleString("en-IN")}</td>
              <td className="p-3">{l.actor_email}</td>
              <td className="p-3 font-mono">{l.action}</td>
              <td className="p-3 text-brand-muted">{l.target || "—"}</td>
            </tr>
          ))}
          {logs.length === 0 && <tr><td colSpan={4} className="text-center p-12 text-brand-muted">No actions logged yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
